import { FederatedPointerEvent, DisplayObject } from 'pixi.js'
import Item from '@/scripts/canvas/Item'
import { isIOS, isDesktopMode } from '@/scripts/helpers/features'
import { ITEM_DELAY } from '@/scripts/config/settings'
import activityInfoBox from '@/scripts/ui/activityInfoBox'
import Analytics, { ACTION_ACTIVITY_HOVER } from '@/scripts/analytics'
import audioManager from '@/scripts/audio/audioManager'
import { onPointerTap } from '@/scripts/helpers/events'
import { getApp } from './sceneManager'
import { openActivityInfoBox, openMobileActivityInfoBox } from './sceneHelpers'
import { centerStageOnItem } from './stagePositioning'
import gsap from 'gsap'
import { SceneState } from './types'

export function setupItemInteractions(state: SceneState): void {
  const { items } = state

  window.addEventListener('keydown', (e) => onEscapePress(e))
  const app = getApp()
  app?.stage.on('pointerup', (e) => onStagePointerUp(e, state))
  app?.stage.on('pointerupoutside', (e) => onStagePointerUp(e, state))

  items.forEach((item) => {
    item.on('pointerover', (e) => handleItemPointerOver(e, state))
    item.on('pointerout', (e) => handleItemPointerOut(e, state))
    item.on('pointermove', () => handleItemPointerMove(state))
    onPointerTap(item, (e) => handleItemPointerUp(e, state))
  })

  document.querySelector('.prev-button')?.addEventListener('click', () => cycleMobileItem(-1, state))
  document.querySelector('.next-button')?.addEventListener('click', () => cycleMobileItem(1, state))
}

function onEscapePress(e: KeyboardEvent): void {
  if (e.key === 'Escape' || e.key === 'Esc') {
    activityInfoBox.hide()
  }
}

function onStagePointerUp(event: FederatedPointerEvent, state: SceneState): void {
  const { touchedItem, phase2HideTimer } = state
  const nativeEvent = (window.event ?? event.originalEvent) as PointerEvent | undefined
  const target = nativeEvent?.target as HTMLElement | null
  if (target && target.closest('.activity-info-box')) return

  stopDragging(state)

  if (!(event.target instanceof Item)) {
    activityInfoBox.hide()
    if (touchedItem) {
      // Kill any pending phase 2 timer to ensure clean state
      phase2HideTimer?.kill()
      state.phase2HideTimer = undefined

      // Ensure item returns to phase 1
      touchedItem.setHoverState(false)
      touchedItem.itemBullet.setHoverState(false)
      state.touchedItem = undefined
    }
  }
}

function startDragging(state: SceneState): void {
  if (!isDesktopMode()) state.isDragging = true
}

function stopDragging(state: SceneState): void {
  state.moveCounter = 0
  if (!isDesktopMode()) state.isDragging = false
}

function handleItemPointerMove(state: SceneState): void {
  state.moveCounter++
  if (!state.isDragging && (state.moveCounter > 20 || isIOS)) startDragging(state)
}

function handleItemPointerUp(event: FederatedPointerEvent, state: SceneState): void {
  const { touchedItem } = state
  if (!isDesktopMode() && state.isDragging) return stopDragging(state)
  stopDragging(state)

  const item = event.target as Item
  if (!(item instanceof Item)) return

  if (!isDesktopMode()) {
    if (touchedItem) {
      touchedItem.setHoverState(false)
      touchedItem.itemBullet.setHoverState(false)
    }
    state.touchedItem = item
    openMobileActivityInfoBox(item, { current: state.itemOverDelayedCall })
    return
  }

  const isTouch = event.pointerType === 'touch'

  // For tablet taps: show phase 2, stop sound, play new sound
  if (isTouch) {
    // Kill any pending hover delayed call (from pointerover)
    state.itemOverDelayedCall?.kill()

    // Cancel any previous phase 2 hide timer and ensure previous item returns to phase 1
    if (state.phase2HideTimer) {
      state.phase2HideTimer.kill()
      state.phase2HideTimer = undefined

      // Ensure previous item returns to phase 1
      if (touchedItem && touchedItem !== item) {
        touchedItem.setHoverState(false)
        touchedItem.itemBullet.setHoverState(false)
      }
    }

    // Stop any currently playing sound immediately
    audioManager.stopItemSfx(500)

    // Show phase 2 (hover state) - but NOT the bullet for touch events
    item.setHoverState(true)
    // Don't show bullet on touch - only on mouse hover
    // item.itemBullet.setHoverState(true)
    // item.itemBullet.parent.addChild(item.itemBullet as DisplayObject)

    // Store the item so handleItemPointerOut knows not to interfere
    state.touchedItem = item

    // Hide phase 2 after 2 seconds - ensure it always completes
    state.phase2HideTimer = gsap.delayedCall(2, () => {
      // Double-check the item is still the touched item before resetting
      if (state.touchedItem === item) {
        item.setHoverState(false)
        item.itemBullet.setHoverState(false)
        state.touchedItem = undefined
      }
      state.phase2HideTimer = undefined
    })
  }

  // Play sound is handled in openActivityInfoBox when playSound is true
  openActivityInfoBox(item, isTouch)
}

function cycleMobileItem(direction: 1 | -1, state: SceneState): void {
  const { touchedItem, sortedItems } = state
  if (!touchedItem) return

  touchedItem.setHoverState(false)
  touchedItem.itemBullet.setHoverState(false)
  activityInfoBox.hide()

  const idx = sortedItems.indexOf(touchedItem)
  const nextIndex = (idx + direction + sortedItems.length) % sortedItems.length
  state.touchedItem = sortedItems[nextIndex]

  centerStageOnItem(state.touchedItem)
  openMobileActivityInfoBox(state.touchedItem, { current: state.itemOverDelayedCall })
}

function handleItemPointerOver(event: FederatedPointerEvent, state: SceneState): void {
  const item = event.target as Item
  if (!(item instanceof Item) || !isDesktopMode()) return

  // Only show bullets on mouse hover, not on touch events
  const isTouch = event.pointerType === 'touch'
  if (isTouch) return

  const alreadyShownForItem =
    (activityInfoBox.isVisible?.() ?? false) && activityInfoBox.getActivity?.()?.positionId === item.id

  if (!alreadyShownForItem) {
    item.setHoverState(true)
    item.itemBullet.setHoverState(true)
    item.itemBullet.parent.addChild(item.itemBullet as DisplayObject)
  }

  state.itemOverDelayedCall?.kill()

  if (!activityInfoBox.isVisible?.()) {
    Analytics.sendCustomEvent(ACTION_ACTIVITY_HOVER, item.activity.linkHash)
    state.itemOverDelayedCall = gsap.delayedCall(ITEM_DELAY / 1000, () => {
      audioManager.playItemSfx(item.id, null)
    })
  }
}

function handleItemPointerOut(event: FederatedPointerEvent, state: SceneState): void {
  const { itemOverDelayedCall, touchedItem, phase2HideTimer } = state
  const item = event.currentTarget as Item
  if (!(item instanceof Item) || !isDesktopMode()) return

  // Don't interfere with phase 2 animation for touch events
  if (touchedItem === item && phase2HideTimer) {
    return
  }

  itemOverDelayedCall?.kill()
  item.setHoverState(false)
  item.itemBullet.setHoverState(false)
  audioManager.stopItemSfx(500)
}
